import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { useIncrementalList } from "../../../shared/hooks/useIncrementalList.js";
import { ListSentinel } from "../../../shared/components/ListSentinel.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { suggestMatch, aliasKeys } from "../utils/matcher.js";
import { loadMatchContext, applyMatch, unmatch, refreshBooks, applyToContext, categoryLabel } from "../services/matchService.js";
import { MatchSheet } from "./MatchSheet.jsx";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const rupeesOf = (t) => (t.creditPaise || t.debitPaise) / 100;

function describe(s) {
  if (!s || s.action === "none") return null;
  if (s.action === "category") return `${categoryLabel(s.category)}`;
  if (s.action === "link") return `🔗 Link to ${s.payment.paymentNo} (already recorded)`;
  const settles = s.allocations.length ? ` → ${s.allocations.map((a) => `${a.invoiceNo} ${inr(a.allocatedAmount)}`).join(", ")}` : "";
  const advance = s.unallocatedAmount > 0 ? ` • ${inr(s.unallocatedAmount)} advance` : "";
  return `${s.party.name}${s.direction === "out" ? " (vendor)" : ""}${settles}${advance}`;
}

/**
 * "To match" tab: each unmatched bank line with a suggested receipt / vendor payment / link / category.
 * One tap accepts a line; "Accept all suggested" accepts every confident one. Nothing is recorded without a tap.
 */
export function MatchQueue({ txns, company, companies, account, fmt, onTxnUpdated, rangeLabel = "", widerRanges = [], onWidenRange }) {
  const { confirmDanger } = useDangerConfirm();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState("open"); // open | matched
  const [busyId, setBusyId] = useState("");
  const [bulk, setBulk] = useState(null); // { done, total, failed: [] }
  const [sheetTxn, setSheetTxn] = useState(null);

  const unmatched = useMemo(() => txns.filter((t) => t.matchStatus !== "matched"), [txns]);
  const matched = useMemo(() => txns.filter((t) => t.matchStatus === "matched").reverse(), [txns]);
  const fromDate = unmatched[0]?.date || txns[0]?.date || "";

  const reloadContext = useCallback(async () => {
    setError("");
    try {
      setCtx(await loadMatchContext({ company, companies, fromDate }));
    } catch (err) {
      console.error("Failed to load matching data:", err);
      setError("Couldn't load customers, bills and payments for matching.");
    }
  }, [company, companies, fromDate]);

  useEffect(() => {
    reloadContext();
  }, [company?.id, account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Oldest line first, each as if the confident ones before it were accepted — so two payments from the
  // same party settle different bills, exactly as "Accept all" will record them
  const suggestions = useMemo(() => {
    const map = new Map();
    if (!ctx) return map;
    let sim = ctx;
    unmatched.forEach((t) => {
      const s = suggestMatch(t, sim);
      map.set(t.id, s);
      if (s.confidence === "high" && (s.action === "create" || s.action === "link")) {
        sim = applyToContext(sim, t, s, { allocations: s.allocations, matchType: s.action === "link" ? "linked" : "created", paymentId: s.payment?.id });
      }
    });
    return map;
  }, [ctx, unmatched]);
  const confident = useMemo(() => unmatched.filter((t) => suggestions.get(t.id)?.confidence === "high"), [unmatched, suggestions]);

  const shown = view === "open" ? [...unmatched].reverse() : matched;
  const list = useIncrementalList(shown, { resetKey: `${account?.id}|${view}` });

  /** Records one match and keeps the context in step, so later suggestions see the new balances. */
  const accept = async (txn, choice, remember, currentCtx) => {
    const result = await applyMatch(txn, choice, { company, account, remember });
    onTxnUpdated(txn.id, {
      matchStatus: "matched",
      matchType: result.matchType,
      paymentId: result.paymentId || null,
      paymentNo: result.paymentNo || null,
      partyId: choice.party?.id || null,
      partyName: choice.party?.name || null,
      category: result.category || null
    });
    let next = applyToContext(currentCtx, txn, choice, result);
    if (remember && choice.party && choice.parsed) {
      const aliases = { ...next.aliases };
      aliasKeys(choice.parsed).forEach((k) => { aliases[k] = { partyId: choice.party.id }; });
      next = { ...next, aliases };
    }
    return next;
  };

  const acceptOne = async (txn, choice, remember = true) => {
    setBusyId(txn.id);
    try {
      setCtx(await accept(txn, choice, remember, ctx));
      if (choice.action === "create") refreshBooks();
    } catch (err) {
      alert(err.message || "Could not save the match.");
    } finally {
      setBusyId("");
    }
  };

  const acceptAll = async () => {
    const queue = [...confident]; // oldest first, the same order the suggestions were worked out in
    let current = ctx;
    const failed = [];
    setBulk({ done: 0, total: queue.length, failed });
    for (let i = 0; i < queue.length; i += 1) {
      const txn = queue[i];
      // Re-check against the latest balances: an earlier line may have settled the same bill
      const s = suggestMatch(txn, current);
      if (s.confidence !== "high") {
        failed.push({ txn, reason: "Needs a look (bills changed)" });
      } else {
        try {
          current = await accept(txn, s, true, current);
        } catch (err) {
          failed.push({ txn, reason: err.message || "Failed" });
        }
      }
      setBulk({ done: i + 1, total: queue.length, failed: [...failed] });
    }
    setCtx(current);
    refreshBooks();
  };

  const doUnmatch = async (txn, { voidIt = false } = {}) => {
    if (voidIt) {
      const ok = await confirmDanger({
        title: `Void ${txn.paymentNo} and unmatch?`,
        message: `${txn.paymentNo} (${inr(rupeesOf(txn))}, ${txn.partyName || ""}) was created from this bank line. Voiding it restores the bills it settled; the line goes back to "To match".`,
        confirmLabel: "Void & unmatch"
      });
      if (!ok) return;
    }
    setBusyId(txn.id);
    try {
      await unmatch(txn, { voidReason: voidIt ? "Unmatched from bank statement" : "" });
      onTxnUpdated(txn.id, { matchStatus: "unmatched", matchType: null, paymentId: null, paymentNo: null, partyId: null, partyName: null, category: null });
      await reloadContext();
    } catch (err) {
      alert(err.message || "Could not unmatch.");
    } finally {
      setBusyId("");
    }
  };

  if (error) return <Card style={{ padding: "1rem", color: "var(--danger)" }}>{error} <Button size="sm" variant="secondary" onClick={reloadContext}>Retry</Button></Card>;
  if (!ctx) return <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>⏳ Reading customers, bills and payments…</div>;

  const running = bulk && bulk.done < bulk.total;

  return (
    <div>
      <div className="filter-pills-row" style={{ marginBottom: "0.5rem" }}>
        <button type="button" className={`filter-pill ${view === "open" ? "active" : ""}`} onClick={() => setView("open")}>Pending ({unmatched.length})</button>
        <button type="button" className={`filter-pill ${view === "matched" ? "active" : ""}`} onClick={() => setView("matched")}>Matched ({matched.length})</button>
      </div>

      {view === "open" && (
        <Card style={{ padding: "0.6rem 0.75rem", marginBottom: "0.6rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "0.82rem" }}>
            {running
              ? `Recording ${bulk.done} of ${bulk.total}…`
              : <><b>{confident.length}</b> suggested ✓ • {unmatched.length - confident.length} need a look</>}
          </div>
          <Button size="sm" variant="primary" disabled={!confident.length || running || !!busyId} onClick={acceptAll}>✓ Accept all {confident.length} suggested</Button>
          {running && (
            <div style={{ width: "100%", height: 6, background: "var(--border-subtle)", borderRadius: 3 }}>
              <div style={{ width: `${(bulk.done / bulk.total) * 100}%`, height: "100%", background: "var(--primary)", borderRadius: 3, transition: "width .2s" }} />
            </div>
          )}
          {bulk && !running && (
            <div style={{ width: "100%", fontSize: "0.78rem", color: bulk.failed.length ? "var(--danger)" : "var(--success)" }}>
              <span data-testid="bulk-result">✓ {bulk.total - bulk.failed.length} recorded</span>{bulk.failed.length ? ` • ${bulk.failed.length} not done: ${bulk.failed.map((f) => `${fmt(f.txn.date)} ${inr(rupeesOf(f.txn))} (${f.reason})`).join("; ")}` : ""}
              <button type="button" className="btn-icon" style={{ marginLeft: 6 }} onClick={() => setBulk(null)} aria-label="Dismiss">✕</button>
            </div>
          )}
        </Card>
      )}

      {shown.length === 0 ? (
        <Card style={{ padding: "1.25rem", textAlign: "center", color: "var(--text-dim)" }}>
          {view === "open"
            ? (txns.length ? `🎉 Every line in ${rangeLabel ? rangeLabel.toLowerCase() : "this period"} is matched.` : `No transactions in ${rangeLabel ? rangeLabel.toLowerCase() : "this period"}.`)
            : "Nothing matched yet."}
          {view === "open" && widerRanges.length > 0 && onWidenRange && (
            <div style={{ marginTop: "0.6rem" }}>
              <div style={{ fontSize: "0.8rem", marginBottom: "0.4rem" }}>Older lines may be waiting:</div>
              <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", flexWrap: "wrap" }}>
                {widerRanges.map((r) => (
                  <button key={r.key} type="button" className="filter-pill" onClick={() => onWidenRange(r.key)}>{r.label}</button>
                ))}
              </div>
            </div>
          )}
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {list.visibleItems.map((t) => {
            const s = suggestions.get(t.id);
            const text = view === "open" ? describe(s) : null;
            const created = t.matchType === "receipt" || t.matchType === "vendorPayment";
            return (
              <div key={t.id} data-testid="match-row" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${t.creditPaise ? "var(--success)" : "var(--danger)"}`, borderRadius: "var(--radius-md)", padding: "0.55rem 0.75rem", opacity: busyId === t.id ? 0.6 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem" }}>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-main)", wordBreak: "break-word", minWidth: 0 }}>{t.description || "—"}</div>
                  <div style={{ fontWeight: 800, flexShrink: 0, color: t.creditPaise ? "var(--success)" : "var(--danger)" }}>{t.creditPaise ? "+" : "−"}{inr(rupeesOf(t))}</div>
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>📅 {fmt(t.date)}{s?.reference ? ` • Ref ${s.reference}` : ""}</div>

                {view === "open" ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "0.8rem", minWidth: 0 }}>
                      {text
                        ? <><span style={{ color: s.confidence === "high" ? "var(--success)" : "var(--warning, #b45309)", fontWeight: 700 }}>{s.confidence === "high" ? "✓ " : "? "}</span>{text}
                          <div style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>{s.reasons.join(" • ")}{s.action === "create" ? ` • ${s.paymentMode}` : ""}</div></>
                        : <span style={{ color: "var(--text-dim)" }}>No match{s?.parsed?.name ? ` for "${s.parsed.name}"` : ""}</span>}
                    </div>
                    <div style={{ display: "flex", gap: "0.35rem", flexShrink: 0 }}>
                      {text && <Button size="sm" variant="primary" disabled={!!busyId || running} onClick={() => acceptOne(t, s)}>Accept</Button>}
                      <Button size="sm" variant="secondary" disabled={!!busyId || running} onClick={() => setSheetTxn(t)}>{text ? "Change" : "Pick party"}</Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginTop: "0.35rem", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "0.8rem" }}>
                      {t.paymentNo
                        ? <>{t.matchType === "linked" ? "🔗" : "✓"} {t.paymentNo} • {t.partyName}</>
                        : <>✓ {categoryLabel(t.matchType)}</>}
                    </div>
                    <div style={{ display: "flex", gap: "0.35rem" }}>
                      {created && <button type="button" className="btn btn-secondary btn-sm" style={{ color: "var(--danger)" }} disabled={!!busyId} onClick={() => doUnmatch(t, { voidIt: true })}>Void & unmatch</button>}
                      <button type="button" className="btn btn-secondary btn-sm" disabled={!!busyId} onClick={() => doUnmatch(t)}>{created ? "Unlink only" : "Unmatch"}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <ListSentinel sentinelRef={list.sentinelRef} hasMore={list.hasMore} visibleCount={list.visibleCount} total={list.total} onShowMore={list.showMore} noun="lines" />
        </div>
      )}

      <MatchSheet
        isOpen={!!sheetTxn}
        onClose={() => setSheetTxn(null)}
        txn={sheetTxn}
        suggestion={sheetTxn ? suggestions.get(sheetTxn.id) : null}
        ctx={ctx}
        fmt={fmt}
        onApply={async (choice, remember) => {
          const txn = sheetTxn;
          setBusyId(txn.id);
          try {
            setCtx(await accept(txn, choice, remember, ctx));
            if (choice.action === "create") refreshBooks();
          } finally {
            setBusyId("");
          }
        }}
      />
    </div>
  );
}

export default MatchQueue;
